import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Phone, Mail, MapPin } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const LinkedinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const YoutubeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);
const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
  </svg>
);

const footerLinks = {
  services: [
    { label: 'Admission Counselling', href: '/contact-us' },
    { label: 'NEET College Predictor', href: '/neet-college-predictor' },
    { label: 'Rank Predictor', href: '/rank-predictor' },
    { label: 'Compare Colleges', href: '/compare-colleges' },
    { label: 'Medical Colleges Database', href: '/medical-colleges-database' },
  ],
  resources: [
    { label: 'NEET Counselling Guide', href: '/neet-counselling-guide' },
    { label: 'State-wise Cutoffs', href: '/medical-colleges-database' },
    { label: 'Choice Filling Strategy', href: '/choice-filling-strategy' },
    { label: 'Blog & Articles', href: '/blog' },
    { label: 'Admission Calendar', href: '/admission-calendar' },
  ],
  company: [
    { label: 'About Us', href: '/about-us' },
    { label: 'Success Stories', href: '/success-stories' },
    { label: 'Contact Us', href: '/contact-us' },
    { label: 'Student Dashboard', href: '/student-dashboard' },
  ],
};

export default function Footer() {
  return (
    <footer className="border-t border-[#0a1f5c]/10 mt-20 bg-[#0a1f5c]">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <Image
                src="/assets/images/WE-LOGO-1-1782729563376.png"
                alt="Wikas Education Logo"
                width={44}
                height={44}
                className="object-contain"
              />
              <span className="font-bold text-lg tracking-tight text-white">
                Wikas <span className="text-blue-300">Education</span>
              </span>
            </div>
            <p className="text-blue-200 text-sm leading-relaxed mb-6 max-w-sm">
              India&apos;s most trusted NEET admission counselling service. Expert guidance for AIQ, State quota, Deemed, and NRI admissions — helping 52,000+ aspirants secure their dream medical college since 2019.
            </p>
            <div className="space-y-2.5">
              <a href="tel:+919742100030" className="flex items-center gap-2.5 text-sm text-blue-200 hover:text-white transition-colors">
                <Phone size={14} className="text-blue-300 flex-shrink-0" />
                <span>+91 97421 00030</span>
              </a>
              <a href="mailto:info@wikasedu.com" className="flex items-center gap-2.5 text-sm text-blue-200 hover:text-white transition-colors">
                <Mail size={14} className="text-blue-300 flex-shrink-0" />
                <span>info@wikasedu.com</span>
              </a>
              <div className="flex items-center gap-2.5 text-sm text-blue-200">
                <MapPin size={14} className="text-blue-300 flex-shrink-0" />
                <span>Bangalore, Karnataka, India</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              {[TwitterIcon, LinkedinIcon, YoutubeIcon, InstagramIcon]?.map((SocialIcon, i) => {
                const Icon = SocialIcon;
                return (
                  <a
                    key={`social-${i}`}
                    href="#"
                    className="w-9 h-9 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center text-blue-200 hover:text-white hover:bg-white/20 transition-all duration-200"
                    aria-label="Social media link"
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Our Services</h4>
            <ul className="space-y-2.5">
              {footerLinks?.services?.map((link) => (
                <li key={`footer-service-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-blue-200 hover:text-white transition-colors duration-200">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Resources</h4>
            <ul className="space-y-2.5">
              {footerLinks?.resources?.map((link) => (
                <li key={`footer-resource-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-blue-200 hover:text-white transition-colors duration-200">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Company</h4>
            <ul className="space-y-2.5">
              {footerLinks?.company?.map((link) => (
                <li key={`footer-company-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-blue-200 hover:text-white transition-colors duration-200">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr className="border-white/10 my-10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-blue-300 text-xs">
            © 2026 Wikas Education. All rights reserved. NEET® is a registered trademark of NTA.
          </p>
          <div className="flex items-center gap-6">
            <Link href="/privacy-policy" className="text-xs text-blue-300 hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-service" className="text-xs text-blue-300 hover:text-white transition-colors">Terms of Service</Link>
            <Link href="/disclaimer" className="text-xs text-blue-300 hover:text-white transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}